/**
 * iOS In-App Purchase Service V2 - Clean Implementation
 * 
 * Simplified, focused implementation following cordova-plugin-purchase best practices.
 * Replaces the bloated 730-line service with essential functionality only.
 */

import { isIOSNativeApp } from '@/utils/platformUtils';
import { productConfigurationService, type IOSProductConfig } from './ProductConfigurationService';
import { supabase } from '@/integrations/supabase/client';
import { appleReceiptValidationService } from './AppleReceiptValidationService';

// Essential types for cordova-plugin-purchase v13
interface Product {
  readonly id: string;
  readonly title?: string;
  readonly transaction?: { id: string; receipt: string };
  getOffer?: () => { order: () => void };
  verify?: () => void; // Verification method
  // v13 structure - price and other data is nested
  readonly raw?: {
    price?: string;
    currency?: string;
  };
  readonly offers?: Array<{
    pricingPhases?: Array<{
      price?: string;
      currency?: string;
    }>;
  }>;
}

interface StoreCallbacks {
  productUpdated: (callback: (product: Product) => void) => StoreCallbacks;
  approved: (callback: (transaction: Product) => void) => StoreCallbacks;
  verified: (callback: (transaction: Product) => void) => StoreCallbacks;
}

interface CordovaStore {
  register: (products: unknown[]) => void;
  initialize: (platforms?: unknown[]) => void;
  get: (id: string) => Product | undefined;
  finish?: (product: Product) => void;
  when: () => StoreCallbacks;
}

interface PurchaseResult {
  success: boolean;
  productId: string;
  transactionId?: string;
  receipt?: string;
  error?: string;
}

/**
 * Clean iOS IAP Service - Essential functionality only
 */
class IOSInAppPurchaseServiceV2 {
  private isIOSNative: boolean;
  private productConfig: readonly IOSProductConfig[];
  private isInitialized = false;
  private currentUserId: string | null = null;
  private purchaseCompletionCallback?: (result: PurchaseResult) => Promise<void>;
  private subscriptionSyncCallback?: () => Promise<void>;

  // CRITICAL FIX: Track processing transactions to prevent rapid duplicates
  private processingTransactions = new Set<string>();

  // CRITICAL FIX: Prevent multiple callback registrations
  private callbacksRegistered = false;

  constructor() {
    this.isIOSNative = isIOSNativeApp();
    this.productConfig = productConfigurationService.getIOSProducts();
  }

  /**
   * Initialize the service using OFFICIAL cordova-plugin-purchase pattern
   */
  async initialize(): Promise<boolean> {
    if (!this.isIOSNative || this.isInitialized) return this.isInitialized;

    try {
      await this.waitForDeviceReady();
      const store = this.getStore();
      if (!store) return false;

      // OFFICIAL PATTERN Step 1: REGISTER products FIRST
      // Use exact constants from CdvPurchase
      const CdvPurchase = (window as unknown as {
        CdvPurchase?: {
          ProductType: { PAID_SUBSCRIPTION: unknown };
          Platform: { APPLE_APPSTORE: unknown };
        }
      }).CdvPurchase;
      if (!CdvPurchase) {
        throw new Error('CdvPurchase not available');
      }

      const productDefinitions = this.productConfig.map(config => ({
        id: config.productId,
        type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
        platform: CdvPurchase.Platform.APPLE_APPSTORE
      }));

      console.log('📦 iOS IAP Service: Registering products:', productDefinitions);
      store.register(productDefinitions);

      // OFFICIAL PATTERN Step 2: SET UP callbacks SECOND
      this.setupCallbacks(store);

      // OFFICIAL PATTERN Step 3: INITIALIZE store LAST
      // Use exact pattern from documentation with platform array
      store.initialize([CdvPurchase.Platform.APPLE_APPSTORE]);

      // Step 4: Process any pending transactions (restore/cleanup)
      console.log('🔄 iOS IAP Service: Processing any pending transactions...');

      this.isInitialized = true;
      console.log('✅ iOS IAP Service: Initialized successfully');
      return true;
    } catch (error) {
      console.error('❌ iOS IAP Service: Initialization failed:', error);
      return false;
    }
  }

  /**
   * Setup store callbacks
   */
  private setupCallbacks(store: CordovaStore): void {
    // CRITICAL FIX: Prevent multiple callback registrations
    if (this.callbacksRegistered) {
      console.log('🔄 iOS IAP Service: Callbacks already registered - skipping duplicate registration');
      return;
    }

    console.log('🔧 iOS IAP Service: Registering callbacks for the first time');

    // Configure validator
    const validatorUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/validate-ios-receipt-v13`;
    (store as unknown as { validator?: { url: string; timeout: number } }).validator = { url: validatorUrl, timeout: 30000 };
    (store as unknown as { applicationUsername?: () => string }).applicationUsername = () => this.currentUserId || 'anonymous';

    // OFFICIAL PATTERN: Use exact syntax from documentation
    // store.when().productUpdated(refreshUI).approved(finishPurchase);
    store.when()
      .productUpdated((product: Product) => {
        // Access properties using cordova-plugin-purchase v13 structure
        const title = product.title;
        const price = (product as unknown as { raw?: { price?: string } }).raw?.price ||
                     (product as unknown as { offers?: Array<{ pricingPhases?: Array<{ price?: string }> }> }).offers?.[0]?.pricingPhases?.[0]?.price;
        const canPurchase = !!(product as unknown as { offers?: unknown[] }).offers?.length;

        console.log(`📦 Product updated from Apple: ${product.id} - ${title} - ${price} - canPurchase: ${canPurchase}`);

        // Log successful product loading
        if (title && price) {
          console.log(`✅ Product ${product.id} loaded successfully with all properties`);
        } else {
          console.error(`❌ CRITICAL: Product ${product.id} missing essential properties:`, {
            title,
            price,
            canPurchase,
            fullProduct: product
          });

          // Check if this is a configuration issue
          const isOurProduct = this.productConfig.some(c => c.productId === product.id);
          if (!isOurProduct) {
            console.error(`❌ Product ${product.id} is not in our configuration!`, {
              ourProducts: this.productConfig.map(c => c.productId)
            });
          }
        }
      })
      .approved(async (product: Product) => {
        // EXTRACT PRODUCT ID: Handle different transaction object structures
        let productId = product.id;

        // For Transaction objects, the product ID is in products[0].id
        if (!productId && (product as unknown as { products?: Array<{ id: string }> }).products?.length) {
          productId = (product as unknown as { products: Array<{ id: string }> }).products[0].id;
        }

        // Fallback to other possible locations
        if (!productId) {
          productId = (product as unknown as { productId?: string }).productId || 'unknown';
        }

        const transactionId = (product as unknown as { transactionId?: string }).transactionId ||
                             product.transaction?.id ||
                             'unknown';

        console.log(`📋 Transaction approved callback triggered: ${productId}`, {
          productObject: product,
          hasId: !!product.id,
          hasProductId: !!(product as unknown as { productId?: string }).productId,
          hasTransaction: !!product.transaction,
          transactionId,
          extractedProductId: productId
        });

        // HANDLE RESTORE/PENDING TRANSACTIONS: Always finish transactions, even test ones
        if (this.isPendingOrTestTransaction(product, productId, transactionId)) {
          console.log(`🔄 Detected pending/test transaction - finishing to clear queue: ${productId} (${transactionId})`);
          this.finishTransaction(product);
          return;
        }

        console.log(`✅ Processing valid new transaction: ${productId}`);

        // CRITICAL FIX: If product ID is still unknown, extract from transaction structure
        let finalProductId = productId;
        if (finalProductId === 'unknown') {
          // Try to extract from transaction products array
          if ((product as unknown as { products?: Array<{ id: string }> }).products?.length) {
            finalProductId = (product as unknown as { products: Array<{ id: string }> }).products[0].id;
            console.log(`🔍 Extracted product ID from transaction structure: ${finalProductId}`);
          } else {
            console.error('❌ CRITICAL: Cannot extract product ID from transaction', product);
            return; // Cannot process without product ID
          }
        }

        await this.handleApprovedPurchase(product, finalProductId);
      })
      .verified(async (product: Product) => {
        // EXTRACT PRODUCT ID: Handle different transaction object structures
        let productId = product.id;

        // For Transaction objects, the product ID is in products[0].id
        if (!productId && (product as unknown as { products?: Array<{ id: string }> }).products?.length) {
          productId = (product as unknown as { products: Array<{ id: string }> }).products[0].id;
        }

        // Fallback to other possible locations
        if (!productId) {
          productId = (product as unknown as { productId?: string }).productId || 'unknown';
        }

        const transactionId = (product as unknown as { transactionId?: string }).transactionId ||
                             product.transaction?.id ||
                             'unknown';

        console.log(`✅ Transaction verified callback triggered: ${productId}`, {
          productObject: product,
          hasReceipt: !!((product as unknown as { receipt?: string }).receipt || product.transaction?.receipt),
          transactionId,
          extractedProductId: productId
        });

        await this.handleVerifiedPurchase(product, productId);
      });

    // CRITICAL FIX: Mark callbacks as registered to prevent duplicates
    this.callbacksRegistered = true;
    console.log('✅ iOS IAP Service: Callbacks registered successfully');
  }

  /**
   * Handle approved purchase - OFFICIAL PATTERN from cordova-plugin-purchase docs
   * Creates subscription mapping and triggers verification
   */
  private async handleApprovedPurchase(product: Product, productId: string): Promise<void> {
    try {
      const transactionId = (product as unknown as { transactionId?: string }).transactionId ||
                           product.transaction?.id ||
                           'unknown';

      console.log(`🔐 Processing approved purchase: ${productId}`, {
        productId,
        transactionId,
        hasReceipt: !!((product as unknown as { receipt?: string }).receipt || product.transaction?.receipt),
        hasUserId: !!this.currentUserId,
        fullProductObject: product
      });

      // CRITICAL FIX: Prevent rapid duplicate processing
      if (transactionId !== 'unknown') {
        if (this.processingTransactions.has(transactionId)) {
          console.log(`🔄 Transaction ${transactionId} already being processed - skipping duplicate`);
          return;
        }

        // Mark as processing
        this.processingTransactions.add(transactionId);

        console.log(`🔍 Checking for existing transaction: ${transactionId}`);
        try {
          const { data: existingTransaction } = await supabase
            .from('cross_platform_transactions')
            .select('id, platform_transaction_id')
            .eq('platform_transaction_id', transactionId)
            .eq('platform', 'ios_iap')
            .single();

          if (existingTransaction) {
            console.log(`✅ Transaction ${transactionId} already exists - skipping duplicate creation`);
            // Still finish the transaction to clear it from the queue
            this.finishTransaction(product);
            // Remove from processing set
            this.processingTransactions.delete(transactionId);
            return;
          }
        } catch (error) {
          // If error is "not found", that's expected - continue with creation
          if (error && typeof error === 'object' && 'code' in error && error.code !== 'PGRST116') {
            console.error('❌ Error checking for existing transaction:', error);
            // Continue anyway - better to risk a duplicate than miss a transaction
          }
        }
      }

      // Create subscription mapping - CRITICAL FOR PREMIUM STATUS
      if (!this.currentUserId) {
        console.error('❌ CRITICAL: No current user ID - cannot create subscription mapping');
      } else if (productId === 'unknown') {
        console.error('❌ CRITICAL: Product ID is unknown - cannot create subscription mapping');
      } else {
        console.log(`🚀 Creating subscription mapping for user ${this.currentUserId} and product ${productId}`);
        try {
          await this.createSubscriptionMappingViaEdgeFunction(product, productId);
          console.log(`✅ Subscription mapping created successfully - user should now be premium`);

          // CRITICAL: Trigger subscription sync callback to refresh permissions
          if (this.subscriptionSyncCallback) {
            console.log('🔄 Triggering subscription sync callback to refresh permissions');
            await this.subscriptionSyncCallback();
          } else {
            console.warn('⚠️ No subscription sync callback available - permissions may not update immediately');
          }
        } catch (error) {
          console.error('❌ CRITICAL: Failed to create subscription mapping - user will NOT be premium:', error);
          // Don't throw here - we still want to complete the transaction
        }
      }

      // Create transaction record immediately (don't wait for verification)
      // Extract receipt if available
      const receipt = (product as unknown as { receipt?: string }).receipt || product.transaction?.receipt;

      if (!this.currentUserId) {
        console.error('❌ CRITICAL: No current user ID - cannot create transaction record');
      } else if (productId === 'unknown') {
        console.error('❌ CRITICAL: Product ID is unknown - cannot create transaction record');
      } else {
        console.log(`🔄 Creating transaction record for user ${this.currentUserId} and transaction ${transactionId}`, {
          hasReceipt: !!receipt,
          receiptLength: receipt?.length || 0
        });
        try {
          await this.createTransactionRecordViaEdgeFunction(productId, transactionId, receipt);
          console.log(`✅ Transaction record created successfully for ${transactionId}`);
        } catch (error) {
          console.error('❌ Failed to create transaction record:', error);
          // Don't throw here - we still want to complete the transaction
        }
      }

      // CRITICAL: Trigger verification to get receipt data (optional for additional validation)
      console.log(`🔍 Triggering verification for product: ${productId}`);
      if (product.verify) {
        product.verify();

        // FALLBACK: If verified callback doesn't fire within 5 seconds, finish transaction anyway
        // The subscription mapping was already created, so user is premium
        setTimeout(() => {
          console.log('⏰ Verification timeout - finishing transaction (user is already premium via subscription mapping)');
          this.finishTransaction(product);
        }, 5000);
      } else {
        console.warn('⚠️ Product.verify() not available - finishing transaction immediately');
        // Still finish the transaction
        this.finishTransaction(product);
      }

      // CRITICAL FIX: Remove from processing set on successful completion
      if (transactionId !== 'unknown') {
        this.processingTransactions.delete(transactionId);
        console.log(`✅ Successfully processed transaction ${transactionId} - removed from processing set`);
      }

    } catch (error) {
      console.error('❌ Error handling approved purchase:', error);
      // Still finish the transaction even on error
      this.finishTransaction(product);
    } finally {
      // CRITICAL FIX: Always remove from processing set to prevent permanent blocking
      const transactionId = (product as unknown as { transactionId?: string }).transactionId ||
                           product.transaction?.id ||
                           'unknown';
      if (transactionId !== 'unknown') {
        this.processingTransactions.delete(transactionId);
        console.log(`🧹 Removed transaction ${transactionId} from processing set`);
      }
    }
  }

  /**
   * Handle verified purchase - OFFICIAL PATTERN from cordova-plugin-purchase docs
   * Creates transaction record with receipt data and finishes transaction
   */
  private async handleVerifiedPurchase(product: Product, productId: string): Promise<void> {
    try {
      const transactionId = (product as unknown as { transactionId?: string }).transactionId ||
                           product.transaction?.id ||
                           'unknown';

      console.log(`✅ Processing verified purchase: ${productId}`, {
        productId,
        transactionId,
        hasReceipt: !!((product as unknown as { receipt?: string }).receipt || product.transaction?.receipt),
        hasUserId: !!this.currentUserId,
        fullProductObject: product
      });

      // Extract receipt from verified transaction
      const receipt = (product as unknown as { receipt?: string }).receipt || product.transaction?.receipt;

      // Validate with Apple (optional - for audit purposes)
      if (receipt && this.currentUserId) {
        await appleReceiptValidationService.validateReceipt(
          receipt,
          this.currentUserId,
          transactionId
        );
      }

      // Create transaction record via edge function with service role authentication
      if (!this.currentUserId) {
        console.error('❌ CRITICAL: No current user ID - cannot create transaction record');
      } else if (productId === 'unknown') {
        console.error('❌ CRITICAL: Product ID is unknown - cannot create transaction record');
      } else {
        console.log(`🔄 Creating transaction record for user ${this.currentUserId} and transaction ${transactionId}`, {
          hasReceipt: !!receipt,
          receiptLength: receipt?.length || 0
        });
        try {
          await this.createTransactionRecordViaEdgeFunction(productId, transactionId, receipt);
          console.log(`✅ Transaction record created successfully for ${transactionId}`);
        } catch (error) {
          console.error('❌ Failed to create transaction record:', error);
          // Don't throw here - we still want to complete the transaction
        }
      }

      // Notify completion
      if (this.purchaseCompletionCallback) {
        await this.purchaseCompletionCallback({
          success: true,
          productId,
          transactionId,
          receipt
        });
      }

      // Trigger sync
      if (this.subscriptionSyncCallback) {
        await this.subscriptionSyncCallback();
      }

      // OFFICIAL PATTERN: Finish the transaction
      this.finishTransaction(product);

    } catch (error) {
      console.error('❌ Error handling verified purchase:', error);
      // Still finish the transaction even on error
      this.finishTransaction(product);
    }
  }

  /**
   * Finish transaction - centralized method
   */
  private finishTransaction(product: Product): void {
    const store = this.getStore();
    if (store && store.finish) {
      // Extract product ID for logging
      let productId = product.id;
      if (!productId && (product as unknown as { products?: Array<{ id: string }> }).products?.length) {
        productId = (product as unknown as { products: Array<{ id: string }> }).products[0].id;
      }
      if (!productId) {
        productId = (product as unknown as { productId?: string }).productId || 'unknown';
      }

      console.log(`🏁 Finishing transaction for product: ${productId}`);
      store.finish(product);
    }
  }

  /**
   * Create subscription mapping via edge function (with service role permissions)
   */
  private async createSubscriptionMappingViaEdgeFunction(product: Product, productId: string): Promise<void> {
    if (!this.currentUserId) {
      throw new Error('No current user ID - cannot create subscription mapping');
    }

    // CRITICAL FIX: Extract transaction ID correctly
    const transactionId = product.transaction?.id || (product as unknown as { transactionId?: string }).transactionId || 'unknown';

    // Map product ID to unified product ID
    const unifiedProductId = productId === 'com.teamjumo.thematmonster.premium.monthly3' ? 'premium_monthly' : 'premium_annual';
    const subscriptionInterval = productId === 'com.teamjumo.thematmonster.premium.monthly3' ? 'monthly' : 'annual';

    console.log(`🚀 Creating subscription mapping via edge function for user ${this.currentUserId} and product ${productId}`);

    try {
      const { data, error } = await supabase.functions.invoke('create-subscription-mapping', {
        body: JSON.stringify({
          userId: this.currentUserId,
          primaryPlatform: 'ios_iap',
          iosOriginalTransactionId: transactionId,
          productId: unifiedProductId,
          subscriptionInterval,
          status: 'active',
          startedAt: new Date().toISOString(),
          currentPeriodStart: new Date().toISOString(),
          currentPeriodEnd: new Date(Date.now() + (subscriptionInterval === 'annual' ? 365 : 30) * 24 * 60 * 60 * 1000).toISOString(),
          syncSource: 'ios_receipt'
        })
      });

      if (error) {
        console.error('❌ Failed to create subscription mapping via edge function:', error);
        throw error;
      }

      console.log(`✅ Subscription mapping created successfully via edge function for ${productId}`, data);
    } catch (error) {
      console.error('❌ Failed to create subscription mapping via edge function:', error);
      throw error; // Re-throw to ensure we know about failures
    }
  }



  /**
   * Create transaction record via edge function with service role authentication
   */
  private async createTransactionRecordViaEdgeFunction(productId: string, transactionId: string, receipt?: string): Promise<void> {
    if (!this.currentUserId) {
      throw new Error('No current user ID - cannot create transaction record');
    }

    console.log(`🔄 Creating transaction record via edge function for user ${this.currentUserId} and transaction ${transactionId}`, {
      hasReceipt: !!receipt,
      receiptLength: receipt?.length || 0
    });

    // Map product ID to unified product ID
    const unifiedProductId = productId === 'com.teamjumo.thematmonster.premium.monthly3' ? 'premium_monthly' : 'premium_annual';
    const subscriptionInterval = productId === 'com.teamjumo.thematmonster.premium.monthly3' ? 'monthly' : 'annual';

    try {
      const { data, error } = await supabase.functions.invoke('create-iap-transaction', {
        body: {
          userId: this.currentUserId,
          platform: 'ios_iap',
          transactionType: 'subscription',
          platformTransactionId: transactionId,
          platformProductId: productId,
          unifiedProductId,
          subscriptionInterval,
          amountCents: productId === 'com.teamjumo.thematmonster.premium.monthly3' ? 1999 : 17900,
          currency: 'USD',
          status: 'completed',
          iosReceipt: receipt || null, // Allow null receipt
          metadata: {
            iosProductId: productId,
            transactionId,
            platform: 'ios-appstore',
            receiptValidated: !!receipt, // Mark as validated only if receipt exists
            receiptAvailable: !!receipt
          }
        }
      });

      if (error) {
        console.error('❌ Edge function error creating transaction record:', error);
        throw error;
      }

      console.log(`✅ Transaction record created via edge function:`, data);
    } catch (error) {
      console.error('❌ Failed to create transaction record via edge function:', error);
      throw error;
    }
  }

  /**
   * Purchase a product using official cordova-plugin-purchase pattern
   */
  async purchaseProduct(productId: string): Promise<PurchaseResult> {
    if (!this.isInitialized) {
      throw new Error('Service not initialized');
    }

    try {
      const store = this.getStore();
      if (!store) {
        throw new Error('Store not available');
      }

      // CRITICAL: Use official pattern from lessons learned (Lines 307-310)
      const product = store.get(productId);
      if (!product) {
        throw new Error(`Product ${productId} not found`);
      }

      const canPurchase = !!product.offers?.length;
      const available = !!product.offers?.length;

      console.log(`🛒 Attempting purchase: ${productId}, canPurchase: ${canPurchase}, available: ${available}`);

      // In sandbox, canPurchase might be undefined but product can still be purchased
      // Allow purchase if canPurchase is true OR if it's undefined (sandbox behavior)
      if (canPurchase === false) {
        throw new Error(`Product ${productId} cannot be purchased (available: ${available})`);
      }

      if (canPurchase === undefined || canPurchase === null) {
        console.log(`⚠️ Product ${productId} has undefined canPurchase (sandbox behavior) - proceeding with purchase attempt`);
      }

      // OFFICIAL PATTERN: product.getOffer().order() (not store.order(productId))
      const offer = product.getOffer?.();
      if (!offer) {
        throw new Error(`No offer available for ${productId}`);
      }

      console.log(`🚀 Initiating purchase with offer.order() for ${productId}`);
      offer.order();
      return { success: true, productId };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(`❌ Purchase failed for ${productId}:`, errorMessage);
      return {
        success: false,
        productId,
        error: errorMessage
      };
    }
  }

  /**
   * Get store instance
   */
  private getStore(): CordovaStore | null {
    const windowWithCordova = window as unknown as { CdvPurchase?: { store?: CordovaStore } };
    return windowWithCordova.CdvPurchase?.store || null;
  }

  /**
   * Wait for device ready
   */
  private async waitForDeviceReady(): Promise<void> {
    return new Promise((resolve) => {
      if (document.readyState === 'complete') {
        resolve();
      } else {
        document.addEventListener('deviceready', () => resolve(), { once: true });
        setTimeout(() => resolve(), 2000); // Fallback
      }
    });
  }

  // Public API methods
  isAvailable(): boolean { return this.isInitialized; }
  setUserId(userId: string | null): void { this.currentUserId = userId; }
  setCurrentUserId(userId: string | null): void { this.currentUserId = userId; }
  setPurchaseCompletionCallback(callback: (result: PurchaseResult) => Promise<void>): void {
    this.purchaseCompletionCallback = callback;
  }

  /**
   * Check if transaction is a pending/test transaction that should be finished and cleared
   */
  private isPendingOrTestTransaction(_product: Product, productId: string, transactionId?: string): boolean {
    // Test transactions with "appstore.application" transaction ID (sandbox artifacts)
    if (transactionId === 'appstore.application') {
      console.log(`🔄 Detected sandbox test transaction: ${transactionId}`);
      return true;
    }

    // ONLY treat as pending if productId is actually invalid
    // Real transactions can have productId 'unknown' in the callback but still be valid
    if (productId === 'com.teamjumo.thematmonster') {
      console.log(`🔄 Detected incomplete transaction with partial bundle ID: ${productId}`);
      return true;
    }

    // If productId is 'unknown' but we have a real transaction ID, this might be a valid transaction
    // Check if it matches our configured products by looking at the transaction structure
    if (productId === 'unknown' && transactionId !== 'appstore.application') {
      console.log(`⚠️ Transaction has unknown product ID but real transaction ID: ${transactionId} - treating as valid`);
      return false; // Don't treat as pending - let it be processed
    }

    // Transactions that don't match our configured products (old/invalid products)
    const isValidProduct = this.productConfig.some(config => config.productId === productId);
    if (!isValidProduct && productId !== 'unknown') {
      console.log(`🔄 Detected transaction for unconfigured product: ${productId}`, {
        configuredProducts: this.productConfig.map(c => c.productId)
      });
      return true;
    }

    return false;
  }


  setSubscriptionSyncCallback(callback: () => Promise<void>): void {
    this.subscriptionSyncCallback = callback;
  }
  getProducts(): Product[] {
    const store = this.getStore();
    if (!store) {
      console.log('🚫 iOS IAP Service: Store not available');
      return [];
    }

    const products = this.productConfig.map(config => {
      const product = store.get(config.productId);
      const hasOffers = !!(product as unknown as { offers?: unknown[] })?.offers?.length;
      console.log(`📦 iOS IAP Service: Product ${config.productId} available:`, hasOffers ? 'yes' : 'not found');
      return product;
    }).filter(Boolean) as Product[];

    console.log(`📦 iOS IAP Service: Returning ${products.length} products out of ${this.productConfig.length} configured`);
    return products;
  }

  // Additional methods for compatibility
  async restorePurchases(): Promise<boolean> {
    // TODO: Implement restore purchases if needed
    console.log('🔄 iOS IAP Service: Restore purchases not yet implemented');
    return false;
  }

  /**
   * Run IAP diagnostic for debugging
   */
  runIAPDiagnostic(): void {
    try {
      const store = this.getStore();
      if (!store) {
        console.log('🚫 IAP Diagnostic: Store not available');
        return;
      }

      console.log('🔍 IAP Diagnostic: Running verification check...');

      // Check products state
      const products = this.getProducts();
      console.log(`📦 IAP Diagnostic: Found ${products.length} products`);

      products.forEach(product => {
        console.log(`📦 IAP Diagnostic: Product ${product.id}:`, {
          available: !!product.offers?.length,
          title: product.title || 'undefined',
          price: product.raw?.price || product.offers?.[0]?.pricingPhases?.[0]?.price || 'undefined',
          canPurchase: !!product.offers?.length,
          hasTransaction: !!product.transaction,
          hasOffer: !!product.getOffer
        });
      });

      console.log('✅ IAP Diagnostic: Verification check complete');
    } catch (error) {
      console.error('❌ IAP Diagnostic: Failed:', error);
    }
  }

  // ===== COMPATIBILITY METHODS FOR EXISTING INTEGRATIONS =====
  // These methods maintain compatibility with existing code that expects them

  /**
   * Get platform information (compatibility method)
   */
  getPlatformInfo(): Record<string, unknown> {
    return {
      isIOSNative: this.isIOSNative,
      isInitialized: this.isInitialized,
      hasStore: !!this.getStore(),
      storeReady: this.isInitialized,
      products: this.getProducts().map(p => ({
        id: p.id,
        title: p.title,
        price: p.raw?.price || p.offers?.[0]?.pricingPhases?.[0]?.price,
        currency: p.raw?.currency || p.offers?.[0]?.pricingPhases?.[0]?.currency || 'USD',
        canPurchase: !!p.offers?.length
      }))
    };
  }

  /**
   * Get transaction state (compatibility method)
   */
  getTransactionState(): Map<string, Record<string, unknown>> {
    const states = new Map();
    this.getProducts().forEach(product => {
      states.set(product.id, {
        available: !!product.offers?.length,
        owned: (product as unknown as { owned?: boolean }).owned,
        canPurchase: !!product.offers?.length
      });
    });
    return states;
  }

  /**
   * Get all transaction states (compatibility method)
   */
  getAllTransactionStates(): Map<string, Record<string, unknown>> {
    return this.getTransactionState();
  }

  /**
   * Clear completed transactions (compatibility method)
   */
  clearCompletedTransactions(): void {
    console.log('clearCompletedTransactions called - no-op in V2 service');
    // V2 service doesn't need manual transaction clearing
  }
}

// Export singleton
export const iosIAPService = new IOSInAppPurchaseServiceV2();
